import { useCallback } from 'react';

import { config } from '../configs';
import { AnalyticsClickEvent, AnalyticsPageView } from '../types/Analytics';

const reportingSuiteIds = config.adobeReportingSuiteIds;

interface AdobeTracking {
  trackPageView: (props: AnalyticsPageView) => void;
  trackClick: (props: AnalyticsClickEvent) => void;
}

const parentName = 'Major League Baseball';

export const useAdobeTracking = (): AdobeTracking => {
  // variables used in both page and click tracking
  const utilizeCoreVariables = useCallback(
    ({ type, subType, viewType }: AnalyticsClickEvent | AnalyticsPageView) => {
      if (typeof window.s_gi === 'undefined' || !reportingSuiteIds) {
        console.error('no window object or report suites');
        return;
      }
      try {
        const s = window.s_gi(reportingSuiteIds);
        window.setAppMeasurementConfig(s);
        s.clearVars();
        s.channel = type;

        // page name (type)
        s.pageName = `${parentName}: ${type}`;

        if (subType) {
          s.pageName += `: ${subType}`;
        }
        if (viewType) {
          s.pageName += `: ${viewType}`;
        }

        // User's okta ID
        s.eVar11 = s.Util.cookieRead('oktaid') || null;
        // User's adobe ID
        s.eVar199 = s.Util.cookieRead('s_ecid') || null;
        // User's session ID
        s.eVar234 = s.Util.cookieRead('btIdentify') || null;
        s.eVar73 = reportingSuiteIds;
        s.eVar229 = 'Webpage';
        return s;
      } catch (error) {
        console.error(`problem setting analytics core variables ${error}`);
      }
    },
    []
  );

  //page view calls
  const trackPageView = useCallback(
    (props: AnalyticsPageView) => {
      const s = utilizeCoreVariables(props);
      if (!s) {
        return;
      }
      const {
        type,
        gameID = '',
        subType = '',
        viewType = '',
        viewSubType = '',
        favTeam = '',
        subPageIndex = '',
        errorMessage = '',
      } = props;
      s.pageURL = `${window.location.origin}${window.location.pathname}`;
      // Game name on game cards
      s.eVar124 = gameID;
      // Top-level grouping of page by overall type
      s.eVar125 = type;
      // Individual pages within a given Page Type
      s.eVar126 = subType;
      // For a Page Type-SubType, if there are multiple views within that - IE. tabs
      s.eVar127 = viewType;
      s.eVar128 = viewSubType;
      s.eVar178 = favTeam;
      // For pages within pages - sub-pages / modals acting as pages.
      s.eVar144 = subPageIndex;
      // For error messages
      s.eVar162 = errorMessage;
      if (type !== undefined) {
        s.t();
      }
    },
    [utilizeCoreVariables]
  );

  // track click calls
  const trackClick = useCallback(
    (props: AnalyticsClickEvent) => {
      const s = utilizeCoreVariables(props);
      if (!s) {
        return;
      }
      const {
        type,
        section = 'content navigation',
        subType = '',
        event = '',
        gameID = '',
        targetURL = '',
        eventIndex = '',
        sectionIndex = '',
        viewType = '',
        tabViewType = '',
        eventText = '',
        errorMessage = '',
      } = props;
      // section type prop
      s.prop12 = section;
      s.prop13 = `${parentName}: ${type}`;
      // track action action name
      s.prop14 = subType
        ? `${parentName}: ${type}: ${subType}: ${event}`
        : `${parentName}: ${type}: ${event}`;
      // gameId
      s.prop17 = gameID;
      s.eVar142 = s.prop17;
      // target url
      s.prop37 = targetURL;
      s.eVar141 = s.prop37;
      // The index (starting at 0) for the element being clicked, within its set of related elements	all Action : Click events
      s.eVar84 = eventIndex;
      s.eVar118 = sectionIndex;
      // Top-level grouping of page by overall type
      s.eVar125 = type;
      // Individual pages within a given Page Type
      s.eVar126 = subType;
      // view type when specified
      s.eVar127 = viewType;
      s.eVar128 = tabViewType;
      // The exact text on the button, link, or image they clicked
      s.eVar140 = eventText;
      // For error messages
      s.eVar162 = errorMessage;
      s.trackExternalLinks = false;
      // to call click tracking only when data is passed
      if (type !== undefined) {
        s.tl(true, 'o', `${parentName} analytics`);
      }
    },
    [utilizeCoreVariables]
  );

  return { trackPageView, trackClick };
};
